import { useRef, useState } from "react";
import { useField } from "formik";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  SegmentedControl,
  Text,
  TextField,
} from "@radix-ui/themes";
import { ChevronDown, ChevronUp, Paperclip, Plus, Trash2 } from "lucide-react";
import { fileService } from "@/services/breeze/FileService";
import { useDebounce } from "@/hooks/useDebounce";
import VariableMenu from "./VariableMenu";
import { WorkflowStep } from "@/types/breeze/Workflow";

interface AttachmentRow {
  file_id: string;
  disposition: "attachment" | "inline";
  content_id?: string;
  filename?: string;
}

interface EmailAttachmentsFieldProps {
  name?: string;
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

export default function EmailAttachmentsField({
  name = "parameters.attachments",
  existingSteps = [],
  triggerEntity,
}: EmailAttachmentsFieldProps) {
  const [field, , helpers] = useField<AttachmentRow[] | undefined>(name);
  const items: AttachmentRow[] = field.value ?? [];
  const [pickerOpenIdx, setPickerOpenIdx] = useState<number | null>(null);

  const setItems = (next: AttachmentRow[]) => helpers.setValue(next);

  const add = () => {
    setItems([...items, { file_id: "", disposition: "attachment" }]);
    setPickerOpenIdx(items.length);
  };
  const remove = (idx: number) => {
    setItems(items.filter((_, i) => i !== idx));
    if (pickerOpenIdx === idx) setPickerOpenIdx(null);
  };
  const update = (idx: number, patch: Partial<AttachmentRow>) =>
    setItems(items.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  return (
    <Box>
      <Flex align="center" gap="2" mb="2">
        <Paperclip size={14} />
        <Text size="2" weight="medium">Attachments</Text>
        <Text size="2" color="gray">
          {items.length} / 10 — total payload capped at 25 MB
        </Text>
      </Flex>

      {items.length === 0 ? (
        <Text size="2" color="gray" mb="2" style={{ display: "block" }}>
          No files attached. Add files from your tenant storage as plain attachments
          or as inline images (CID). You can also reference a file ID dynamically with
          a workflow expression like <code>{`{{$anythink.steps.upload.data.id}}`}</code>.
        </Text>
      ) : (
        <Flex direction="column" gap="2" mb="2">
          {items.map((r, idx) => (
            <AttachmentEditor
              key={`row-${idx}`}
              row={r}
              isPickerOpen={pickerOpenIdx === idx}
              onPickerToggle={() => setPickerOpenIdx(pickerOpenIdx === idx ? null : idx)}
              onChange={(patch) => update(idx, patch)}
              onRemove={() => remove(idx)}
              alreadyUsed={items.map((x) => x.file_id).filter((_, i) => i !== idx)}
              existingSteps={existingSteps}
              triggerEntity={triggerEntity}
            />
          ))}
        </Flex>
      )}

      <Button type="button" variant="soft" size="2" onClick={add} disabled={items.length >= 10}>
        <Plus size={14} /> Add attachment
      </Button>
    </Box>
  );
}

interface AttachmentEditorProps {
  row: AttachmentRow;
  isPickerOpen: boolean;
  onPickerToggle: () => void;
  onChange: (patch: Partial<AttachmentRow>) => void;
  onRemove: () => void;
  alreadyUsed: string[];
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
}

function AttachmentEditor({
  row,
  isPickerOpen,
  onPickerToggle,
  onChange,
  onRemove,
  alreadyUsed,
  existingSteps,
  triggerEntity,
}: AttachmentEditorProps) {
  const isExpression = row.file_id.includes("{{");
  const fileIdRef = useRef<HTMLInputElement>(null);
  const contentIdRef = useRef<HTMLInputElement>(null);

  const insertAt = (
    ref: React.RefObject<HTMLInputElement>,
    current: string,
    text: string,
    apply: (next: string) => void,
  ) => {
    const el = ref.current;
    if (el) {
      const start = el.selectionStart ?? current.length;
      const end = el.selectionEnd ?? current.length;
      const next = current.slice(0, start) + text + current.slice(end);
      apply(next);
      setTimeout(() => {
        el.focus();
        const caret = start + text.length;
        el.setSelectionRange(caret, caret);
      }, 0);
    } else {
      apply(current + text);
    }
  };

  return (
    <Card variant="surface">
      <Box p="3">
        <Flex direction="column" gap="2">
          {/* Disposition + remove */}
          <Flex align="center" justify="between" gap="2">
            <Box>
              <Text size="1" color="gray" mb="1" style={{ display: "block" }}>
                Disposition
              </Text>
              <SegmentedControl.Root
                size="1"
                value={row.disposition}
                onValueChange={(v) => onChange({ disposition: v as "attachment" | "inline" })}
              >
                <SegmentedControl.Item value="attachment">Attached</SegmentedControl.Item>
                <SegmentedControl.Item value="inline">Inline (CID)</SegmentedControl.Item>
              </SegmentedControl.Root>
            </Box>
            <IconButton type="button" variant="ghost" color="gray" onClick={onRemove} aria-label="Remove attachment">
              <Trash2 size={14} />
            </IconButton>
          </Flex>

          {/* File ID + variable picker + browse */}
          <Box>
            <Flex align="center" justify="between" mb="1">
              <Text size="2" weight="medium">File</Text>
              {isExpression && <Badge color="amber" variant="soft">workflow expression</Badge>}
              {!isExpression && row.filename && (
                <Text size="2" color="gray" style={{
                  maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {row.filename}
                </Text>
              )}
            </Flex>
            <Flex gap="2" align="center">
              <TextField.Root
                ref={fileIdRef}
                size="2"
                placeholder="File ID or {{$anythink.steps.upload.data.id}}"
                value={row.file_id}
                onChange={(e) => onChange({ file_id: e.target.value, filename: undefined })}
                style={{ flex: 1 }}
              />
              <VariableMenu
                existingSteps={existingSteps}
                triggerEntity={triggerEntity}
                onInsert={(text) =>
                  insertAt(fileIdRef, row.file_id, text, (next) =>
                    onChange({ file_id: next, filename: undefined }),
                  )
                }
              />
              <Button type="button" variant="soft" size="2" color="gray" onClick={onPickerToggle}>
                {isPickerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {isPickerOpen ? "Hide files" : "Browse files"}
              </Button>
            </Flex>
          </Box>

          {/* Content ID — inline disposition only */}
          {row.disposition === "inline" && (
            <Box>
              <Flex align="center" justify="between" mb="1">
                <Text size="2" weight="medium">Content ID</Text>
                <Text size="1" color="gray">
                  Matches <code>{`<img src="cid:…">`}</code> in the template
                </Text>
              </Flex>
              <Flex gap="2" align="center">
                <TextField.Root
                  ref={contentIdRef}
                  size="2"
                  placeholder="e.g. logo.png"
                  value={row.content_id ?? ""}
                  onChange={(e) => onChange({ content_id: e.target.value })}
                  style={{ flex: 1 }}
                />
                <VariableMenu
                  existingSteps={existingSteps}
                  triggerEntity={triggerEntity}
                  onInsert={(text) =>
                    insertAt(contentIdRef, row.content_id ?? "", text, (next) =>
                      onChange({ content_id: next }),
                    )
                  }
                />
              </Flex>
            </Box>
          )}
        </Flex>

        {isPickerOpen && (
          <Box mt="3">
            <InlineFilePicker
              alreadyUsed={alreadyUsed}
              onPick={(id, filename) => {
                onChange({ file_id: String(id), filename });
                onPickerToggle();
              }}
            />
          </Box>
        )}
      </Box>
    </Card>
  );
}

function InlineFilePicker({
  alreadyUsed, onPick,
}: {
  alreadyUsed: string[];
  onPick: (id: number, filename: string) => void;
}) {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  const { data, isLoading } = useQuery({
    queryKey: ["any-files-inline", debounced],
    queryFn: () => fileService.getAll(1, 25, undefined, "asc", debounced || undefined),
  });
  const items: any[] = (data as any)?.items ?? (Array.isArray(data) ? data : []);

  return (
    <Box style={{
      border: "1px solid var(--gray-5)", borderRadius: 6, overflow: "hidden",
    }}>
      <Box p="2" style={{ borderBottom: "1px solid var(--gray-5)" }}>
        <TextField.Root
          size="2"
          placeholder="Search files in this tenant's storage…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </Box>
      <Box style={{ maxHeight: 260, overflowY: "auto" }}>
        {isLoading ? (
          <Box p="3"><Text size="2" color="gray">Searching…</Text></Box>
        ) : items.length === 0 ? (
          <Box p="3"><Text size="2" color="gray">No files.</Text></Box>
        ) : (
          items.map((f: any) => {
            const taken = alreadyUsed.includes(String(f.id));
            return (
              <Flex
                key={f.id}
                align="center"
                justify="between"
                style={{
                  padding: "8px 12px",
                  borderBottom: "1px solid var(--gray-4)",
                  cursor: taken ? "default" : "pointer",
                  opacity: taken ? 0.5 : 1,
                }}
                onClick={() => !taken && onPick(f.id, f.original_file_name || `file #${f.id}`)}
              >
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Text size="2" weight="medium" style={{ display: "block" }}>
                    {f.original_file_name || `file #${f.id}`}
                  </Text>
                  <Text size="2" color="gray">
                    {f.file_type} · {prettyBytes(f.file_size)}
                  </Text>
                </Box>
                {taken && <Badge color="gray" variant="soft">added</Badge>}
              </Flex>
            );
          })
        )}
      </Box>
    </Box>
  );
}

function prettyBytes(n: number | undefined) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
